import {
  Show,
  Suspense,
  createContext,
  createEffect,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";

// import Buoy from "@/components/Base/Buoy/Buoy.tsx";
import { Loading } from "@/components/Base/Loading/Loading";
import { useDialog } from "@/components/Dialogs/useDialog.ts";
import { useHeaderOpacityOnScroll } from "@/hooks/useScroll";
import Cookies from "@/layouts/h5/components/Cookies";
import { debounce } from "@/lib/generalUtils.ts";
import { getRealPath } from "@/lib/language";
import { deviceInfo } from "@/stores/deviceStore";
import { FlowProps } from "@/types/base";
import { useLocation } from "@solidjs/router";

import AppDownloadBanner from "./components/AppDownloadBanner";
import Footer from "./components/Footer";
import HomeHeader from "./components/Header/HomeHeader";
import BottomNavigation from "./components/Navigation/BottomNav";
import VerifyTip from "./components/VerifyTip";

// 定义 Context 的类型
interface BottomNavContextType {
  showBottomNav: () => boolean;
  setShowBottomNav: (value: boolean) => void;
}

export const Context = createContext<BottomNavContextType>({
  showBottomNav: () => false,
  setShowBottomNav: () => {},
});

export function H5LobbyLayout(props: FlowProps) {
  let scrollContainer!: HTMLDivElement;

  const hiddenFooter = ["menu", "profile", "sports"];

  const { operateDialog } = useDialog();
  const location = useLocation();

  useHeaderOpacityOnScroll();

  onMount(() => {
    if (!localStorage.getItem("mionrs18Accepted")) {
      operateDialog("Minors18Popup", true);
    }
  });

  // 底部导航显示状态
  const [showBottomNav, setShowBottomNav] = createSignal(true);
  const [lastScrollY, setLastScrollY] = createSignal(0);
  const [isRouteSwitching, setIsRouteSwitching] = createSignal(false);
  const HIDE_THRESHOLD_PX = 5; // 超过5px才触发隐藏
  const IDLE_SHOW_DELAY_MS = 250; // 停止滚动250ms后显示

  // 停止滚动后显示（防抖）
  const showOnIdle = debounce(() => setShowBottomNav(true), IDLE_SHOW_DELAY_MS);

  // 滚动处理函数
  const handleContentScroll = () => {
    if (isRouteSwitching()) return; // 路由切换时忽略滚动
    const currentScrollY = window.scrollY;
    const previousScrollY = lastScrollY();

    // 向下或向上滚动且超过阈值 -> 立即隐藏
    if (Math.abs(currentScrollY - previousScrollY) > HIDE_THRESHOLD_PX) {
      setShowBottomNav(false);
    }

    setLastScrollY(currentScrollY);

    // 停止滚动后显示
    showOnIdle();
  };

  // 在组件挂载后添加滚动监听
  onMount(() => {
    // 监听 window 的 Y 轴滚动事件
    window.addEventListener("scroll", handleContentScroll);
    onCleanup(() => {
      window.removeEventListener("scroll", handleContentScroll);
    });
  });

  // 路由变化时重置状态
  createEffect(() => {
    void location.pathname;
    // 路由变化：临时禁用滚动响应并强制显示底部导航
    setIsRouteSwitching(true);
    setShowBottomNav(true);
    setLastScrollY(window.scrollY);
    setTimeout(() => setIsRouteSwitching(false), 400);
  });

  return (
    <div
      class={`h-screen-full overflow-y-auto ${deviceInfo.ios && deviceInfo.m_app == 10 ? "page-scroll-container" : ""}`}
      ref={scrollContainer}
    >
      <div class="fixed left-0 right-0 top-0 z-50 h-[48px]">
        <HomeHeader />
      </div>
      <div class="safe-main-padding min-h-screen pt-[48px]">
        <Suspense
          fallback={
            <div class="min-h-[calc(100vh-var(--header-height))]">
              <Loading />
            </div>
          }
        >
          <Context.Provider value={{ showBottomNav, setShowBottomNav }}>
            {props.children}
          </Context.Provider>
          <Show
            when={
              !hiddenFooter.includes(
                getRealPath(useLocation().pathname).split("/")[0],
              )
            }
            fallback={<></>}
          >
            <Footer currentPath={useLocation().pathname} />
          </Show>
        </Suspense>
        <AppDownloadBanner />
      </div>
      {/*<Cookies />*/}
      <VerifyTip />
      <div class="fixed bottom-0 left-0 right-0 z-50">
        <BottomNavigation />
      </div>
    </div>
  );
}
